import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { useMutation, gql } from '@apollo/client'
import { GoogleLogin } from '@react-oauth/google'
import './HomePage.css'

const REQUEST_OTP = gql`
  mutation RequestOtp($phone: String!) {
    requestOtp(phone: $phone) {
      success
      message
      otpSent
      isNewUser
      expiresInSeconds
      otpDebug
    }
  }
`;

const VERIFY_OTP = gql`
  mutation VerifyOtp($phone: String!, $otp: String!, $name: String, $email: String) {
    verifyOtp(phone: $phone, otp: $otp, name: $name, email: $email) {
      success
      token
      message
      user {
        id
        name
        phone
        email
        role
      }
    }
  }
`;

const SOCIAL_LOGIN = gql`
  mutation SocialLogin($input: SocialLoginInput!) {
    socialLogin(input: $input) {
      success
      token
      message
      user { id name phone email role avatarUrl }
    }
  }
`;

const HomePage = ({ user: appUser, onAuthSuccess }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const returnLocation = location?.state?.from
  const returnTo = returnLocation
    ? `${returnLocation.pathname || '/products'}${returnLocation.search || ''}${returnLocation.hash || ''}`
    : location?.state?.returnTo || '/products'

  const [phone, setPhone] = useState(() => {
    if (typeof window === 'undefined') return ''
    return localStorage.getItem('lastPhoneNumber') || ''
  })
  const [otp, setOtp] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [otpRequested, setOtpRequested] = useState(false)
  const [isNewUser, setIsNewUser] = useState(false)
  const [otpInfoMessage, setOtpInfoMessage] = useState('')
  const [otpDebug, setOtpDebug] = useState('')
  const [error, setError] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [facebookName, setFacebookName] = useState('')
  const [facebookId, setFacebookId] = useState('')
  const [facebookPicture, setFacebookPicture] = useState('')
  const [facebookChecking, setFacebookChecking] = useState(false)
  const [fbReady, setFbReady] = useState(Boolean(typeof window !== 'undefined' && window.__FB_SDK_READY__))
  const [user, setUser] = useState(null)
  const [showAuthModal, setShowAuthModal] = useState(false)

  const currentUser = appUser ?? user
  const isLoggedIn = Boolean(currentUser)
  const googleClientIdConfigured = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID)
  const facebookAppId = import.meta.env.VITE_FACEBOOK_APP_ID || null

  const otpButtonLabel = useMemo(() => (!otpRequested ? 'Request OTP' : 'Verify OTP'), [otpRequested])

  useEffect(() => {
    if (!isLoggedIn && location.state?.openAuth) {
      setShowAuthModal(true)

      const { openAuth, authRequest, ...remainingState } = location.state
      navigate(location.pathname, {
        replace: true,
        state: remainingState
      })
      return
    }

    if (!isLoggedIn && location.state?.from) {
      setShowAuthModal(true)
    }
  }, [isLoggedIn, location.pathname, location.state, navigate])

  useEffect(() => {
    if (appUser?.phone) {
      setPhone(appUser.phone)
    }
  }, [appUser?.phone])

  const onAuthSuccessLocal = (userObj, token, remember) => {
    setUser(userObj || null)
    if (typeof onAuthSuccess === 'function') {
      onAuthSuccess(userObj || null, token, remember)
    }

    try {
      if (token) {
        if (remember) {
          localStorage.setItem('token', token)
          localStorage.removeItem('authToken')
          sessionStorage.removeItem('authToken')
          if (typeof window !== 'undefined') delete window.__SESSION_TOKEN__
        } else {
          if (typeof window !== 'undefined') window.__SESSION_TOKEN__ = token
          sessionStorage.setItem('authToken', token)
          localStorage.removeItem('authToken')
        }
      }
    } catch (e) {
      // ignore storage errors
    }
  }


  const handleFacebookLogin = () => {
    setError('')

    if (!facebookAppId) {
      setError('Facebook login is not configured yet.')
      return
    }

    const doFbLogin = () => {
      if (!window.FB) {
        setError('Facebook login is still loading. Please try again.')
        return
      }

      setFacebookChecking(true)

      window.FB.login(
        (response) => {
          const accessToken = response?.authResponse?.accessToken

          if (!accessToken) {
            setFacebookChecking(false)
            setError('Facebook login was cancelled or failed.')
            return
          }

          window.FB.api(
            '/me',
            {
              fields: 'id,name,email,picture.type(large)'
            },
            (profile) => {
              setFacebookChecking(false)

              if (profile?.id) {
                setFacebookId(profile.id)
              }

              if (profile?.name) {
                setFacebookName(profile.name)
              }

              if (profile?.picture?.data?.url) {
                setFacebookPicture(profile.picture.data.url)
              }

              const normalizedPhone = phone
                .replace(/\D/g, '')
                .slice(0, 10)

              socialLogin({
                variables: {
                  input: {
                    provider: 'facebook',
                    accessToken,
                    phone:
                      normalizedPhone.length === 10
                        ? normalizedPhone
                        : null
                  }
                }
              })
            }
          )
        },
        {
          scope: 'public_profile,email'
        }
      )
    }

    if (window.FB) {
      doFbLogin()
      return
    }

    setOtpInfoMessage('Waiting for Facebook to load...')

    let waited = 0

    const interval = setInterval(() => {
      if (window.FB) {
        clearInterval(interval)
        setOtpInfoMessage('')
        doFbLogin()
        return
      }

      waited += 200

      if (waited >= 5000) {
        clearInterval(interval)
        setOtpInfoMessage('')
        setError('Facebook SDK is still loading. Please try again.')
      }
    }, 200)
  }

  useEffect(() => {
    const scriptId = 'facebook-jssdk'

    if (!facebookAppId) {
      setFbReady(false)
      return
    }

    if (!document.getElementById('fb-root')) {
      const fbRoot = document.createElement('div')
      fbRoot.id = 'fb-root'
      document.body.appendChild(fbRoot)
    }

    let initialized = false

    const initializeFacebook = () => {
      if (initialized || !window.FB) return

      try {
        window.FB.init({
          appId: facebookAppId,
          cookie: true,
          xfbml: true,
          version: 'v20.0'
        })

        initialized = true
        window.__FB_SDK_READY__ = true
        setFbReady(true)
      } catch (err) {
        console.error('Facebook SDK initialization failed', err)
        setFbReady(false)
      }
    }

    window.fbAsyncInit = initializeFacebook

    if (window.FB) {
      initializeFacebook()
      return
    }

    const existingScript = document.getElementById(scriptId)

    if (existingScript) {
      const poll = setInterval(() => {
        if (window.FB) {
          clearInterval(poll)
          initializeFacebook()
        }
      }, 100)

      const timeout = setTimeout(() => clearInterval(poll), 10000)

      return () => {
        clearInterval(poll)
        clearTimeout(timeout)
      }
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.defer = true
    document.body.appendChild(script)

    return () => {
      if (window.fbAsyncInit === initializeFacebook) {
        delete window.fbAsyncInit
      }
    }
  }, [facebookAppId])

  const [requestOtp, { loading: requestOtpLoading }] = useMutation(REQUEST_OTP, {
    onCompleted: (data) => {
      const response = data?.requestOtp
      if (!response?.success) {
        return
      }

      setOtpRequested(true)
      setIsNewUser(Boolean(response.isNewUser))
      setOtpInfoMessage(response.message)
      setOtpDebug(response.otpDebug || '')
    },
    onError: (err) => setError(err.message)
  })

  const [verifyOtp, { loading: verifyOtpLoading }] = useMutation(VERIFY_OTP, {
    onCompleted: (data) => {
      const response = data?.verifyOtp
      if (response?.success && response?.token && response?.user) {
        onAuthSuccessLocal(response.user, response.token, rememberMe)
        navigate(returnTo)
      }
    },
    onError: (err) => {
      const message = err?.message || ''

      if (message.toLowerCase().includes('expired')) {
        setOtpRequested(false)
        setOtp('')
        setOtpDebug('')
        setOtpInfoMessage('OTP expired. Please request a new OTP.')
        setError('')
        return
      }

      setError(message)
    }
  })

  const [socialLogin, { loading: socialLoginLoading }] = useMutation(SOCIAL_LOGIN, {
    onCompleted: (data) => {
      const response = data?.socialLogin
      if (!response) {
        setError('Unexpected response from server during social login.')
        console.error('socialLogin onCompleted no response', data)
        return
      }

      if (!response.success) {
        setError(response.message || 'Social login failed on server.')
        console.error('socialLogin failed', response)
        return
      }

      const needsPhoneUpdate = Boolean(response.token && !response.user?.phone)

      // Keep the token only for the profile mutation; do not authenticate the app yet.
      if (needsPhoneUpdate) {
        if (typeof window !== 'undefined') window.__PROFILE_TOKEN__ = response.token
        navigate('/profile', { state: { returnTo, user: response.user, authToken: response.token, remember: rememberMe } })
        return
      }

      // If token present, set auth and return to original route
      if (response.token) {
        try {
          onAuthSuccessLocal(response.user, response.token, rememberMe)
        } catch (e) {
          console.error('onAuthSuccessLocal threw', e)
        }
        navigate(returnTo)
        return
      }

      // No token — do not set auth state yet. Send user to Profile to collect phone.
      navigate('/profile', { state: { returnTo, user: response.user, remember: rememberMe } })
    },
    onError: (err) => {
      setError(err.message || 'Social login failed due to network or server error.')
      console.error('socialLogin onError', err)
    }
  })

  useEffect(() => {
    if (!showAuthModal || isLoggedIn || !fbReady || !window.FB) {
      return
    }

    window.FB.getLoginStatus((response) => {
      const accessToken = response?.authResponse?.accessToken

      if (!accessToken) {
        return
      }

      window.FB.api(
        '/me',
        {
          fields: 'id,name,picture.type(large)'
        },
        (profile) => {
          if (!profile) return

          if (profile.id) {
            setFacebookId(profile.id)
          }

          if (profile.name) {
            setFacebookName(profile.name)
          }

          if (profile.picture?.data?.url) {
            setFacebookPicture(profile.picture.data.url)
          }
        }
      )
    })
  }, [showAuthModal, isLoggedIn, fbReady])

  const isBusy = requestOtpLoading || verifyOtpLoading || socialLoginLoading

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    const normalizedPhone = phone.replace(/\D/g, '').slice(0, 10)
    if (normalizedPhone.length !== 10) {
      setError('Please enter a valid 10-digit mobile number')
      return
    }

    if (!otpRequested) {
      localStorage.setItem('lastPhoneNumber', normalizedPhone)
      requestOtp({ variables: { phone: normalizedPhone } })
      return
    }

    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP')
      return
    }

    const normalizedEmail = email.trim().toLowerCase()
    if (normalizedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError('Please enter a valid email address if provided')
      return
    }

    verifyOtp({
      variables: {
        phone: normalizedPhone,
        otp,
        name: name || null,
        email: normalizedEmail || null
      }
    })
  }

  const handleGoogleSuccess = (credentialResponse) => {
    // Two possible shapes: { credential } from Google JWT flow, or { access_token } from OAuth access token flow
    setError('')

    if (credentialResponse?.credential) {
      // credential is a JWT (id_token) — decode payload to extract name/picture
      try {
        const payload = JSON.parse(atob(credentialResponse.credential.split('.')[1]))
        // optional: we could store provider profile info for UI, but skip here
      } catch (err) {
        // ignore decode errors
      }

      const normalizedPhone = phone.replace(/\D/g, '').slice(0, 10)
      socialLogin({
        variables: {
          input: {
            provider: 'google',
            idToken: credentialResponse.credential,
            phone: normalizedPhone.length === 10 ? normalizedPhone : null
          }
        }
      })
      return
    }

    if (credentialResponse?.access_token) {
      // fetch profile using access_token
      fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${credentialResponse.access_token}` }
      })
        .then((r) => r.json())
        .then((profile) => {
          // no-op: optionally use profile info for UI
        })
        .catch(() => { });

      const normalizedPhone = phone.replace(/\D/g, '').slice(0, 10)
      socialLogin({
        variables: {
          input: {
            provider: 'google',
            accessToken: credentialResponse.access_token,
            phone: normalizedPhone.length === 10 ? normalizedPhone : null
          }
        }
      });
      return
    }

    setError('Unable to login with Google. Please try again.')
  }


  const features = [
    { icon: 'flash_on', title: 'Instant Recharge', desc: 'Top-up in minutes with no delay' },
    { icon: 'lock', title: 'Secure Payment', desc: 'Safe, protected transactions' },
    { icon: 'public', title: 'All Operators', desc: 'Airtel, Jio, Vi, BSNL and more' },
    { icon: 'receipt_long', title: 'Utility Bill Payments', desc: 'Pay electricity, water, broadband and more' },
    { icon: 'support_agent', title: '24/7 Support', desc: 'Fast help whenever you need it' },
    { icon: 'verified', title: 'No Hidden Charges', desc: 'Clear pricing and instant activation' }
  ]

  const popularDeals = [
    { tag: 'Popular Recharge', operator: 'Jio', price: '₹199', title: 'Unlimited data + calling', subtitle: 'Best for everyday usage' },
    { tag: 'Smart Recharge', operator: 'Airtel', price: '₹299', title: 'Weekend data boost', subtitle: 'Great for streaming & work' },
    { tag: 'Value Recharge', operator: 'Vi', price: '₹599', title: 'Long validity combo', subtitle: 'For heavy users' }
  ]

  return (
    <div className="homepage">
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-badges">
            <span>Mobile recharge</span>
            <span>Utility bill payments</span>
          </div>
          <div className="hero-text">
            <h1>Recharge and pay bills with ease.</h1>
            <p>Recharge mobile numbers and pay utility bills with trusted service, secure payment, and instant confirmation.</p>
          </div>

          <div className="hero-actions">
            <button type="button" className="btn-primary-modern" onClick={() => navigate('/products')}>Browse recharge plans</button>
            {!isLoggedIn && (
              <button type="button" className="btn-secondary-modern" onClick={() => setShowAuthModal(true)}>Sign in</button>
            )}
            <button type="button" className="btn-secondary-modern" onClick={() => navigate('/about')}>Why MoRecharz</button>
          </div>

          <div className="home-plans-notice" role="status">
            <span className="material-icons">info</span>
            <span>Test plans only. Live recharge plans are coming soon. Thank you for your patience.</span>
          </div>

          <div className="hero-stats">
            <div className="stat-pill">
              <strong>2 min</strong>
              <span>avg. activation</span>
            </div>
            <div className="stat-pill">
              <strong>24/7</strong>
              <span>support</span>
            </div>
            <div className="stat-pill">
              <strong>100%</strong>
              <span>secure checkout</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="promo-strip">
          {popularDeals.map((deal) => (
            <div key={deal.operator} className="deal-card">
              <span className="deal-tag">{deal.tag}</span>
              <div className="deal-header">
                <span className="deal-operator">{deal.operator}</span>
                <strong>{deal.price}</strong>
              </div>
              <h3>{deal.title}</h3>
              <p>{deal.subtitle}</p>
              <button type="button" className="deal-link" onClick={() => navigate('/products')}>View recharge plan</button>
            </div>
          ))}
        </div>

        {showAuthModal && !isLoggedIn && (
          <div className="auth-modal-backdrop" role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowAuthModal(false)
          }}>
            <div className="auth-section auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
              <div className="auth-card-modern">
                <div className="auth-modal-toolbar">
                  <div className="auth-header">
                    <h2 id="auth-modal-title">Secure login</h2>
                    <p>Login or register with WhatsApp OTP in a few seconds</p>
                  </div>
                  <button type="button" className="auth-modal-close" onClick={() => setShowAuthModal(false)} aria-label="Close sign in">
                    <span className="material-icons">close</span>
                  </button>
                </div>
                <div className="auth-modal-content">
                  <>
                    <form onSubmit={handleSubmit} className="auth-form">
                      <div className="form-group">
                        <label>WhatsApp number</label>
                        <input
                          type="tel"
                          placeholder="Enter 10-digit mobile number"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          maxLength="10"
                          disabled={isBusy}
                        />
                      </div>

                      {otpRequested && (
                        <>
                          <div className="form-group">
                            <label>OTP</label>
                            <input
                              type="text"
                              placeholder="Enter 6-digit OTP"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              maxLength="6"
                              disabled={isBusy}
                            />
                          </div>

                          {isNewUser && (
                            <>
                              <div className="form-group">
                                <label>Full name (optional)</label>
                                <input
                                  type="text"
                                  placeholder="Your name"
                                  value={name}
                                  onChange={(e) => setName(e.target.value)}
                                  disabled={isBusy}
                                />
                              </div>
                              <div className="form-group">
                                <label>Email address (optional)</label>
                                <input
                                  type="email"
                                  placeholder="your@email.com"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value)}
                                  disabled={isBusy}
                                />
                              </div>
                            </>
                          )}
                        </>
                      )}

                      {otpInfoMessage && <div className="info-message">{otpInfoMessage}</div>}
                      {otpDebug && <div className="debug-message">Development OTP: <strong>{otpDebug}</strong></div>}
                      {error && <div className="error-message">{error}</div>}

                      <button type="submit" className="btn-primary-modern" disabled={isBusy}>
                        {isBusy ? 'Please wait...' : otpButtonLabel}
                      </button>

                      <div className="form-group remember-me">
                        <label>
                          <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} /> Keep me signed in
                        </label>
                      </div>

                      {otpRequested && (
                        <button
                          type="button"
                          className="btn-secondary-modern"
                          onClick={() => {
                            setOtpRequested(false)
                            setIsNewUser(false)
                            setOtp('')
                            setOtpDebug('')
                            setOtpInfoMessage('')
                            setError('')
                          }}
                          disabled={isBusy}
                        >
                          Edit WhatsApp number
                        </button>
                      )}
                    </form>

                    <div className="social-divider"><span>or continue with</span></div>

                    <div className="socials social-buttons">

                      {/* Google - first row */}
                      <div className="social-col">
                        {googleClientIdConfigured ? (
                          <div className="social-google-wrapper">
                            <GoogleLogin
                              onSuccess={handleGoogleSuccess}
                              onError={() => setError('Google login failed. Please try again.')}
                              width="100%"
                            />
                          </div>
                        ) : (
                          <button
                            className="social-button social-google"
                            type="button"
                            disabled
                          >
                            Configure Google Client ID
                          </button>
                        )}
                      </div>

                      {/* Facebook - second row */}
                      <div className="social-col social-facebook-col">

                        <button
                          type="button"
                          className="social-button social-facebook"
                          onClick={handleFacebookLogin}
                          disabled={isBusy || facebookChecking}
                        >
                          {facebookPicture ? (
                            <img
                              src={facebookPicture}
                              alt=""
                              className="facebook-profile-picture"
                            />
                          ) : (
                            <span className="social-facebook-mark">f</span>
                          )}

                          <span className="facebook-button-text">
                            {facebookChecking
                              ? 'Connecting to Facebook...'
                              : facebookName
                                ? `Continue as ${facebookName}`
                                : 'Continue with Facebook'}
                          </span>
                        </button>

                      </div>

                    </div>
                  </>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="features-section">
          <div className="section-header">
            <h2>Why MoRecharz?</h2>
            <p>Experience the future of mobile recharges</p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-tile">
                <div className="feature-icon-box">
                  <span className="material-icons">{feature.icon}</span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}
export default HomePage
